import React, { useEffect, useState } from 'react';
import ATMBrandLogo from '../atoms/ATMBrandLogo/ATMBrandLogo.component';
import { ATMMenu } from '../atoms/ATMMenu/ATMMenu.component';
import { ATMGrid } from '../atoms/ATMGrid/ATMGrid.component';
import { useNavigate, useLocation } from 'react-router-dom';
import FooterComponent from '../Footer/footer.component';
import styles from './private.module.scss';
import { fetchAuthSession } from 'aws-amplify/auth';
import { logout, hasAuth, validateAuth, signIn } from 'libraries/amplify.library';
import { useUser } from 'contexts/UserContext';
import { loggedInUserMenuOptions } from 'constants/HomePage/home';
import { MOLIdleTime } from '../molecules/MOLIdleTime/MOLIdleTime.component';
import { ATMLoader } from '../atoms/ATMLoader/ATMLoader.component';


// Routes that do NOT require authentication (no forced login redirect).
const PUBLIC_PATHS = ['/', '/reviewAndSubmit'];

const PrivateLayout: React.FC<any> = ({ children }) => {

    const [selectedValue, setSelectedValue] = useState('');
    const [isChecking, setIsChecking] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const navigateTo = useNavigate();
    const location = useLocation();
    const { isUserAuthenticated, userFullName } = useUser();


    useEffect(() => {
        const isPublicRoute = PUBLIC_PATHS.includes(location.pathname);

        const checkAuth = async () => {
            // For public routes, skip auth check entirely unless there's an OAuth callback code
            if (isPublicRoute) {
                const urlParams = new URLSearchParams(window.location.search);
                const code = urlParams.get('code');
                
                // If no OAuth code present, public route renders immediately without auth check
                if (!code) {
                    setIsChecking(false);
                    setIsAuthenticated(false);
                    return;
                }
            }

            // Quick presence check first
            if (!hasAuth()) {
                const urlParams = new URLSearchParams(window.location.search);
                const code = urlParams.get('code');

                if (!code) {
                    // Protected routes: just mark as not authenticated
                    // User will see a message or be redirected by route guard
                    console.log('User not authenticated on protected route');
                    setIsChecking(false);
                    return;
                }
                // If we have a code, let the Hub listener handle it
                setIsChecking(false);
                return;
            }

            // Validate token authenticity and expiration
            const authStatus = await validateAuth();

            if (!authStatus) {
                // Protected routes: mark as not authenticated
                console.log('Token validation failed on protected route');
                setIsChecking(false);
                return;
            }

            setIsAuthenticated(authStatus);
            setIsChecking(false);
        };

        checkAuth();
    }, [location]);


    // Handle OAuth callback
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        if (error) {
            console.error('OAuth Error:', error, errorDescription);
            return;
        }

        if (code) {
            console.log('OAuth code detected:', code);
            console.log('Current URL:', window.location.href);

            // Check if we've already processed this code
            const processedCode = sessionStorage.getItem('processed_oauth_code');
            if (processedCode === code) {
                console.log('Code already processed, skipping...');
                return;
            }

            console.log('Processing authentication...');
            sessionStorage.setItem('processed_oauth_code', code);

            // Amplify should automatically handle the code exchange
            // Force a session fetch to trigger the Hub event
            fetchAuthSession({ forceRefresh: true })
                .then((session) => {
                    console.log('Session after code exchange:', session);
                    if (!session.tokens) {
                        console.error('No tokens in session - OAuth exchange failed');
                        // Clear the processed code so user can retry
                        sessionStorage.removeItem('processed_oauth_code');
                    }
                    return session;
                })
                .catch((error) => {
                    console.error('Error processing OAuth code:', error);
                    console.error('Error details:', JSON.stringify(error, null, 2));
                    // Clear the processed code so user can retry
                    sessionStorage.removeItem('processed_oauth_code');
                    return error;
                });
        }
    }, [location]);

    const handleSignOut = async () => {
        await logout();
    };

    // Public routes render immediately; protected routes only render once auth is confirmed
    // so the protected UI never flashes before a redirect to login.
    const isPublicRoute = PUBLIC_PATHS.includes(location.pathname);
    const isProtectedRoute = location.pathname === '/admin';
    
    // Redirect unauthenticated users trying to access /admin back to home
    useEffect(() => {
        if (isProtectedRoute && !isChecking && !isAuthenticated) {
            console.log('Unauthorized access to /admin, redirecting to home...');
            navigateTo('/');
        }
    }, [isProtectedRoute, isChecking, isAuthenticated, navigateTo]);
    
    const canRenderChildren = isPublicRoute || (!isChecking && isAuthenticated);


    const handleMenuSelect = async (menuOption: any) => {
        setSelectedValue(menuOption.key);
        if (menuOption.key === 'home') {
            navigateTo('/');
        } else if (menuOption.key === 'Admin' || menuOption.key === 'AdminLogin' || menuOption.key === 'admin') {
            // Check if user is authenticated before navigating to admin
            if (!isAuthenticated) {
                // Trigger SSO login
                await signIn();
            } else {
                navigateTo('/admin');
            }
        } else {
            window.open(menuOption.path, '_blank');
        }
    };


    const renderList = (list: any) => {
        return list?.map((menu_option: any) => (
            <ATMMenu.Item
                key={menu_option.key}
                name={menu_option.key}
                active={selectedValue === menu_option.key}
                onClick={() => handleMenuSelect(menu_option)}
                content={menu_option.name}
            />
        ));
    };

    return (
        <>
            <ATMGrid fluid vertical className={styles.container}>
                <ATMGrid.Row>
                    <ATMGrid.Column computer={2} tablet={4} mobile={16}>
                        <a
                            className={styles.brandLogo}
                            href="https://www.socalgas.com/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            <ATMBrandLogo
                                height={40}
                                width={102.26}
                                color="#ffffff"
                                logoName={'socal gas'}
                            />
                        </a>
                    </ATMGrid.Column>

                    <ATMGrid.Column
                        className={styles.alignMenus}
                        computer={14}
                        tablet={12}
                        mobile={16}
                    >

                        <ATMMenu secondary size="small">
                            {renderList(loggedInUserMenuOptions.filter(opt => opt.key !== 'AdminLogin'))}

                            {isUserAuthenticated && (

                                <>
                                    <ATMMenu.Item
                                        position='right'
                                        name="userName"
                                        content={`Welcome ${userFullName}`}
                                        style={{ cursor: 'default', pointerEvents: 'none' }}
                                    />
                                    <ATMMenu.Item

                                        name="signout"
                                        content="Sign Out"
                                        onClick={handleSignOut}
                                    />
                                </>

                            )}

                        </ATMMenu>

                    </ATMGrid.Column>
                </ATMGrid.Row>
            </ATMGrid>

            {/* child components */}
            <div className={styles.wrapper}>
                {canRenderChildren ? (
                    children
                ) : (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '400px',
                        }}
                    >
                        <ATMLoader active inline="centered">Loading...</ATMLoader>
                    </div>
                )}
            </div>

            {isUserAuthenticated && (
                <MOLIdleTime
                    timeout={Number(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES) || 25}
                />
            )}

            {/* footer component */}
            {location.pathname !== '/admin' && <FooterComponent />}
        </>
    )
}

export default PrivateLayout;